<script lang="ts">
  import { fade, scale, fly } from 'svelte/transition';
  import { cubicOut, backOut, elasticOut } from 'svelte/easing';
  import type { Config, GameOption, ScreenState } from './types';
  import SelectionScreen from './SelectionScreen.svelte';
  import LoadingScreen from './LoadingScreen.svelte';
  import SuccessScreen from './SuccessScreen.svelte';
  import OfferScreen from './OfferScreen.svelte';
  import { playTransitionSound } from './sounds';

  export let config: Config;
  export let demoScreen: ScreenState | null = null;

  let currentScreen: ScreenState = 'selection';
  let selectedOption: GameOption | null = null;
  let isFirstRender = true;

  // For demo mode, use first option as default
  $: activeScreen = demoScreen ?? currentScreen;
  $: activeOption = demoScreen ? (selectedOption ?? config.options[0]) : selectedOption;

  // Track previous screen for directional transitions and play sound
  let previousScreen: ScreenState = 'selection';
  $: {
    if (activeScreen !== previousScreen) {
      // Play transition sound (skip on initial render)
      if (!isFirstRender) {
        playTransitionSound();
      }
      isFirstRender = false;
      previousScreen = activeScreen;
    }
  }

  function handleSelect(event: CustomEvent<GameOption>) {
    selectedOption = event.detail;
    currentScreen = 'loading';
  }

  function handleLoadingComplete() {
    currentScreen = 'success';
  }

  function handleSuccessComplete() {
    currentScreen = 'offer';
  }

  function handleReturn() {
    currentScreen = 'selection';
    selectedOption = null;
  }

  // Transition configurations for premium feel
  const transitions = {
    // Selection screen - gentle fade in, zoom out when leaving
    selectionIn: { duration: 400, easing: cubicOut },
    selectionOut: { duration: 300, start: 1, opacity: 0 },

    // Loading screen - slide up with energy
    loadingIn: { duration: 450, y: 30, easing: backOut },
    loadingOut: { duration: 250, easing: cubicOut },

    // Success screen - burst in with scale
    successIn: { duration: 500, start: 0.8, easing: elasticOut },
    successOut: { duration: 200, easing: cubicOut },

    // Offer screen - dramatic slide up reveal
    offerIn: { duration: 500, y: 40, easing: backOut },
    offerOut: { duration: 300, y: -20, easing: cubicOut }
  };
</script>

<div class="campaign-card-container">
  <div class="campaign-card">
    {#if activeScreen === 'selection'}
      <div
        class="screen-wrapper"
        in:fade={transitions.selectionIn}
        out:scale={transitions.selectionOut}
      >
        <SelectionScreen
          options={config.options}
          selectionTitle={config.selectionTitle}
          selectionBackgroundHex={config.selectionBackgroundHex}
          image={config.image}
          cardTitle={config.cardTitle}
          on:select={handleSelect}
        />
      </div>
    {/if}

    {#if activeScreen === 'loading' && activeOption}
      <div
        class="screen-wrapper"
        in:fly={transitions.loadingIn}
        out:fade={transitions.loadingOut}
      >
        <LoadingScreen
          selectedOption={activeOption}
          loadingText={config.loadingText}
          loadingDurationMs={demoScreen ? 999999 : config.loadingDurationMs}
          image={config.image}
          cardTitle={config.cardTitle}
          on:complete={handleLoadingComplete}
        />
      </div>
    {/if}

    {#if activeScreen === 'success' && activeOption}
      <div
        class="screen-wrapper"
        in:scale={transitions.successIn}
        out:fade={transitions.successOut}
      >
        <SuccessScreen
          selectedOption={activeOption}
          loadingFinishedTitle={config.loadingFinishedTitle}
          loadingFinishedBackgroundHex={config.loadingFinishedBackgroundHex}
          image={config.image}
          cardTitle={config.cardTitle}
          on:complete={handleSuccessComplete}
        />
      </div>
    {/if}

    {#if activeScreen === 'offer' && activeOption}
      <div
        class="screen-wrapper"
        in:fly={transitions.offerIn}
        out:fly={transitions.offerOut}
      >
        <OfferScreen
          selectedOption={activeOption}
          offerScreenKickerText={config.offerScreenKickerText}
          offerScreenTitleText={config.offerScreenTitleText}
          offerScreenReturnText={config.offerScreenReturnText}
          offerScreenCountdownSeconds={demoScreen ? 999999 : config.offerScreenCountdownSeconds}
          copyBox={config.copyBox}
          image={config.image}
          cardTitle={config.cardTitle}
          on:return={handleReturn}
        />
      </div>
    {/if}
  </div>
</div>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

  .campaign-card-container {
    width: 100%;
    height: 100%;
    container-type: size;
  }

  .campaign-card {
    width: 100%;
    height: 100%;
    background: #1a1a2e;
    border-radius: 6.4px;
    overflow: hidden;
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.08),
      inset 0 0 0 1px rgba(255, 255, 255, 0.05);
    position: relative;
    font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  /* Premium glare/shine effect */
  .campaign-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.15) 0%,
      rgba(255, 255, 255, 0.05) 25%,
      transparent 50%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 10;
    border-radius: 6.4px;
  }

  .screen-wrapper {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
</style>
